<template>
  <div>
    <ClinicPageHeader
      title="Acompanhamento"
      subtitle="Pacientes sem retorno, oportunidades e campanhas de reativação"
    >
      <template #actions>
        <button
          v-if="canEdit('acompanhamento')"
          class="btn btn--primary btn--sm"
          @click="newCampaign"
        >
          + Nova campanha
        </button>
      </template>
    </ClinicPageHeader>

    <div class="dbody">
      <!-- Sub-navegação de acompanhamento -->
      <div class="tabs" style="margin-bottom:var(--space-6)">
        <router-link
          class="tab"
          active-class="tab--active"
          exact
          :to="{ name: 'ClinicNoReturn' }"
        >
          Sem retorno
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          exact
          :to="{ name: 'ClinicOpportunities' }"
        >
          Oportunidades
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          exact
          :to="{ name: 'ClinicCampaigns' }"
        >
          Campanhas
        </router-link>
      </div>

      <!-- Erro -->
      <ClinicStateBox
        v-if="state === 'error'"
        state="error"
        title="Não foi possível carregar as campanhas"
        message="Ocorreu um erro ao buscar as campanhas de reativação. Tente novamente."
      >
        <template #actions>
          <button class="btn btn--primary btn--sm" @click="reload">
            Tentar de novo
          </button>
        </template>
      </ClinicStateBox>

      <!-- Loading -->
      <template v-else-if="state === 'loading'">
        <div class="card" style="margin-bottom:var(--space-6)">
          <div class="skel-line" style="width:45%;margin-bottom:20px" />
          <div class="skel-line" style="width:100%;height:64px" />
        </div>
        <div class="camp-board" style="margin-bottom:var(--space-6)">
          <div
            v-for="i in 4"
            :key="i"
            class="skel-line"
            style="width:100%;height:180px;border-radius:var(--radius-lg)"
          />
        </div>
      </template>

      <!-- Conteúdo -->
      <template v-else>
        <!-- Fluxo da campanha -->
        <div class="card" style="margin-bottom:var(--space-6)">
          <div class="card-head">
            <h3>Fluxo da campanha · {{ activeCampaign.name }}</h3>
            <span class="badge badge--info">
              {{ activeCampaign.statusLabel }}
            </span>
          </div>
          <div class="camp-flow">
            <template v-for="(step, i) in flowSteps">
              <div :key="step.title" class="camp-step">
                <span class="n">{{ i + 1 }}</span>
                <div class="tx">
                  <b>{{ step.title }}</b>
                  <span>{{ step.detail }}</span>
                </div>
              </div>
              <svg
                v-if="i < flowSteps.length - 1"
                :key="step.title + '-arrow'"
                class="camp-arrow"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M5 12h14M13 6l6 6-6 6"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </template>
          </div>
        </div>

        <!-- Funil kanban -->
        <div class="card-head">
          <h3
            style="font-size:var(--font-size-md);font-weight:var(--fw-bold);color:var(--color-gray-900)"
          >
            Funil da campanha
          </h3>
        </div>
        <div class="camp-board" style="margin-bottom:var(--space-6)">
          <div
            v-for="col in board"
            :key="col.key"
            class="camp-col"
            :class="col.key"
          >
            <div class="camp-col-head">
              <span class="t">{{ col.title }}</span>
              <span class="n">{{ col.chips.length }}</span>
            </div>
            <div v-for="chip in col.chips" :key="chip.name" class="camp-chip">
              <div class="avatar avatar--sm" :class="chip.av">
                {{ chip.initials }}
              </div>
              <div class="info">
                <b>{{ chip.name }}</b>
                <span>{{ chip.meta }}</span>
              </div>
            </div>
            <p
              v-if="!col.chips.length"
              style="font-size:var(--font-size-xs);color:var(--color-gray-400);text-align:center;padding:var(--space-4) 0;margin:0"
            >
              Nenhum paciente
            </p>
          </div>
        </div>

        <!-- Lista de campanhas -->
        <div class="card card--flush">
          <div class="card-head flush">
            <h3>Campanhas</h3>
            <button
              v-if="canEdit('acompanhamento')"
              class="btn btn--primary btn--sm"
              @click="newCampaign"
            >
              + Nova campanha
            </button>
          </div>

          <table
            v-if="campaigns.length"
            class="table"
            style="margin-top:var(--space-2)"
          >
            <thead>
              <tr>
                <th>Campanha</th>
                <th>Público</th>
                <th>Enviadas</th>
                <th>Retornos</th>
                <th>Conversão</th>
                <th>Status</th>
                <th class="t-right">Ação</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in campaigns" :key="c.name">
                <td class="cell-strong">{{ c.name }}</td>
                <td class="num">{{ c.audience }}</td>
                <td class="num">{{ c.sent }}</td>
                <td class="num">{{ c.returns }}</td>
                <td class="num">
                  <b :style="{ color: c.conversionColor }">
                    {{ c.conversion }}
                  </b>
                </td>
                <td>
                  <span class="badge" :class="`badge--${c.statusVariant}`">
                    {{ c.status }}
                  </span>
                </td>
                <td class="t-right">
                  <button
                    v-if="canEdit('acompanhamento') && c.stage === 'rascunho'"
                    data-test="send-campaign"
                    class="btn btn--primary btn--sm"
                    :disabled="c.sending"
                    @click="send(c)"
                  >
                    {{ c.sending ? "Enviando…" : "Enviar" }}
                  </button>
                  <button
                    v-else-if="
                      canEdit('acompanhamento') && c.stage === 'em_andamento'
                    "
                    data-test="finish-campaign"
                    class="btn btn--outline btn--sm"
                    :disabled="c.sending"
                    @click="finish(c)"
                  >
                    Encerrar
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <ClinicStateBox
            v-else
            state="empty"
            title="Nenhuma campanha criada"
            message="Crie uma campanha de reativação para começar a acompanhar retornos."
          />
        </div>
      </template>
    </div>

    <ClinicNewCampaign
      v-if="showNewCampaign"
      @close="showNewCampaign = false"
      @created="onCampaignCreated"
    />
  </div>
</template>

<script>
import clinicCan from "@/mixins/clinicCan";
import { ClinicCampaigns as CampaignsService } from "@/services/clinica";
import ClinicPageHeader from "@/components/clinica/ClinicPageHeader.vue";
import ClinicStateBox from "@/components/clinica/ClinicStateBox.vue";
import ClinicNewCampaign from "@/components/clinica/ClinicNewCampaign.vue";

// stage do backend → variante de badge da tabela
const STAGE_VARIANT = {
  em_andamento: "info",
  rascunho: "neutral",
  concluida: "success",
  concluída: "success"
};

export default {
  name: "ClinicCampaigns",
  components: { ClinicPageHeader, ClinicStateBox, ClinicNewCampaign },
  mixins: [clinicCan],
  data() {
    return {
      // Estado de carga (loading/ready/error). Preview de erro via ?state=error.
      state: "loading",
      activeCampaign: { name: "—", statusLabel: "", audienceCount: 0 },
      // Passos do fluxo: rótulos de processo (não são registros do backend).
      flowSteps: [
        { title: "Selecionar público", detail: "Pacientes elegíveis" },
        { title: "Mensagem", detail: "WhatsApp + SMS" },
        { title: "Acompanhar", detail: "Aberturas e respostas" },
        { title: "Agendar retorno", detail: "Converter em consulta" }
      ],
      board: [],
      campaigns: [],
      // modal de criação (substitui o window.prompt)
      showNewCampaign: false
    };
  },
  created() {
    this.fetch();
  },
  methods: {
    async fetch() {
      this.state = "loading";
      try {
        const fail = this.$route.query.state === "error";
        const [listRes, funnelRes] = await Promise.all([
          CampaignsService.list({ fail }),
          CampaignsService.funnel({ fail })
        ]);
        this.campaigns = listRes.data.map(c => this.mapCampaign(c));
        this.board = this.buildBoard(funnelRes.data);
        const active =
          listRes.data.find(c => c.stage === "em_andamento") || listRes.data[0];
        if (active) {
          this.activeCampaign = {
            name: active.name,
            statusLabel: active.status,
            audienceCount: active.audienceCount
          };
          this.flowSteps[0].detail = `${active.audienceCount} pacientes`;
        }
        this.state = "ready";
      } catch (e) {
        this.state = "error";
      }
    },
    buildBoard(funnel) {
      const map = chips =>
        (chips || []).map(c => ({
          patientId: c.patientId,
          name: c.name,
          initials: c.initials,
          av: c.avatarClass,
          meta: c.meta
        }));
      return [
        {
          key: "is-start",
          title: "Selecionados",
          chips: map(funnel.selecionados)
        },
        {
          key: "is-sent",
          title: "Mensagem enviada",
          chips: map(funnel.enviada)
        },
        { key: "is-open", title: "Respondeu", chips: map(funnel.respondeu) },
        {
          key: "is-booked",
          title: "Retorno agendado",
          chips: map(funnel.agendado)
        }
      ];
    },
    mapCampaign(c) {
      const hasConversion = c.conversion != null;
      return {
        id: c.id,
        name: c.name,
        audience: c.audienceCount,
        sent: c.sent != null ? c.sent : "—",
        returns: c.returns != null ? c.returns : "—",
        conversion: hasConversion ? c.conversion : "—",
        conversionColor: hasConversion
          ? "var(--color-success)"
          : "var(--color-gray-400)",
        status: c.status,
        stage: c.stage,
        statusVariant: STAGE_VARIANT[c.stage] || "neutral",
        sending: false
      };
    },
    reload() {
      this.fetch();
    },
    newCampaign() {
      this.showNewCampaign = true;
    },
    // Encerra a campanha (running → done).
    async finish(campaign) {
      try {
        const { data } = await CampaignsService.finish(campaign.id);
        Object.assign(campaign, this.mapCampaign(data));
        this.$toast.success("Campanha encerrada");
      } catch (e) {
        this.$toast.error("Não foi possível encerrar a campanha");
      }
    },
    // Campanha criada pelo modal (service já devolve o shape adaptado).
    onCampaignCreated(data) {
      this.showNewCampaign = false;
      this.campaigns.push(this.mapCampaign(data));
    },
    // Dispara a campanha (WhatsApp+SMS via Zenvia no backend): move
    // selected→sent. Atualiza a linha e recarrega o funil.
    async send(campaign) {
      if (campaign.sending) return;
      campaign.sending = true;
      try {
        const { data } = await CampaignsService.send(campaign.id);
        Object.assign(campaign, this.mapCampaign(data));
        const funnelRes = await CampaignsService.funnel();
        this.board = this.buildBoard(funnelRes.data);
        this.$toast.success("Campanha enviada");
      } catch (e) {
        campaign.sending = false;
        this.$toast.error("Não foi possível enviar a campanha");
      }
    }
  }
};
</script>
